import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { formatNumber } from '@/lib/utils'
import {
  Activity,
  Server,
  Database,
  HardDrive,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
} from 'lucide-react'

interface ServiceStatus {
  name: string
  status: 'healthy' | 'degraded' | 'down'
  latency: number
  lastChecked: string
}

interface QueueStatus {
  name: string
  depth: number
  processedLastHour: number
  errorRate: number
}

interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'down'
  services: ServiceStatus[]
  queues: QueueStatus[]
  metrics: {
    requestsLastHour: number
    avgLatency: number
    errorRate: number
    activeConnections: number
  }
  storage: {
    d1RowsRead: number
    d1RowsWritten: number
    kvReads: number
    kvWrites: number
    r2Requests: number
  }
}

export function SystemHealthPage() {
  const [health, setHealth] = useState<SystemHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchHealth = async () => {
    try {
      const data = await api.get<SystemHealth>('/system/health')
      setHealth(data)
    } catch (error) {
      console.error('Failed to fetch system health:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchHealth()
    const interval = setInterval(fetchHealth, 30000) // Refresh every 30s
    return () => clearInterval(interval)
  }, [])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchHealth()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'degraded':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      case 'down':
        return <XCircle className="h-5 w-5 text-red-500" />
      default:
        return null
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500/10 text-green-500 border-green-500/20'
      case 'degraded':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
      case 'down':
        return 'bg-red-500/10 text-red-500 border-red-500/20'
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Health</h1>
          <p className="text-muted-foreground">
            Monitor platform infrastructure and services
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      <div
        className={`p-6 rounded-lg border ${getStatusColor(health?.overall || 'healthy')}`}
      >
        <div className="flex items-center gap-4">
          {getStatusIcon(health?.overall || 'healthy')}
          <div>
            <h2 className="text-lg font-semibold capitalize">
              System {health?.overall || 'Unknown'}
            </h2>
            <p className="text-sm opacity-80">
              {health?.overall === 'healthy'
                ? 'All systems operational'
                : health?.overall === 'degraded'
                  ? 'Some services experiencing issues'
                  : 'System is currently down'}
            </p>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="bg-card border border-border rounded-lg">
        <div className="border-b border-border px-4 py-3 flex items-center gap-2">
          <Server className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Services</h2>
        </div>
        <div className="divide-y divide-border">
          {health?.services.map((service) => (
            <div
              key={service.name}
              className="px-4 py-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(service.status)}
                <span className="font-medium">{service.name}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{service.latency}ms</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(service.status)}`}
                >
                  {service.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Queues */}
      <div className="bg-card border border-border rounded-lg">
        <div className="border-b border-border px-4 py-3 flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Queues</h2>
        </div>
        <div className="divide-y divide-border">
          {health?.queues.map((queue) => (
            <div key={queue.name} className="px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{queue.name}</span>
                <span
                  className={`text-sm ${
                    queue.errorRate > 5 ? 'text-red-500' : 'text-muted-foreground'
                  }`}
                >
                  {queue.errorRate.toFixed(2)}% error rate
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Queue Depth: </span>
                  <span className="font-medium">{formatNumber(queue.depth)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Processed/hr: </span>
                  <span className="font-medium">
                    {formatNumber(queue.processedLastHour)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Requests/hr</p>
              <p className="text-xl font-bold">
                {formatNumber(health?.metrics.requestsLastHour || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Avg Latency</p>
              <p className="text-xl font-bold">
                {health?.metrics.avgLatency || 0}ms
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={`h-5 w-5 ${
                (health?.metrics.errorRate || 0) > 1 ? 'text-red-500' : 'text-yellow-500'
              }`}
            />
            <div>
              <p className="text-sm text-muted-foreground">Error Rate</p>
              <p className="text-xl font-bold">
                {(health?.metrics.errorRate || 0).toFixed(2)}%
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Server className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-sm text-muted-foreground">Connections</p>
              <p className="text-xl font-bold">
                {formatNumber(health?.metrics.activeConnections || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Metrics */}
      <div className="bg-card border border-border rounded-lg">
        <div className="border-b border-border px-4 py-3 flex items-center gap-2">
          <Database className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-semibold">Storage (Last Hour)</h2>
        </div>
        <div className="p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <p className="text-sm text-muted-foreground">D1 Rows Read</p>
            <p className="text-lg font-bold">
              {formatNumber(health?.storage.d1RowsRead || 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">D1 Rows Written</p>
            <p className="text-lg font-bold">
              {formatNumber(health?.storage.d1RowsWritten || 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">KV Reads</p>
            <p className="text-lg font-bold">
              {formatNumber(health?.storage.kvReads || 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">KV Writes</p>
            <p className="text-lg font-bold">
              {formatNumber(health?.storage.kvWrites || 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">R2 Requests</p>
            <p className="text-lg font-bold">
              {formatNumber(health?.storage.r2Requests || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
